'use client'

import Image from 'next/image'
import Link from 'next/link'
import { ExchangeTabs } from '@/components/exchanges/ExchangeTabs'
import type { ExchangeType, FeesTier, RegionRestriction } from '@/lib/exchangesMeta'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ExchangePageData {
  detail: {
    id: string
    name: string
    year_established: number | null
    country: string | null
    description: string | null
    url: string
    image: string
    facebook_url: string
    reddit_url: string
    telegram_url: string
    twitter_handle: string
    trust_score: number
    trust_score_rank: number
    trade_volume_24h_btc: number
    centralized: boolean
  }
  meta: {
    type: ExchangeType
    feesTier: FeesTier
    makerFee: number
    takerFee: number
    kycRequired: boolean
    derivatives: boolean
    regionRestriction: RegionRestriction
    featured?: boolean
    referralBonus?: string
    referralUrl?: string
    referralCode?: string
    description?: string
  }
  about: string
  volumeChartUsd: [number, number][]
  topPairs: {
    base: string
    target: string
    last: number
    volume: number
    converted_last: { usd: number }
    converted_volume: { usd: number }
    bid_ask_spread_percentage: number
    trade_url: string | null
  }[]
  btcPrice: number
  volume24hUsd: number
  volumeChange7d?: number | null
  slug: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const sf = "var(--font-saira), 'Saira', sans-serif"
const MUTED = '#a1a1aa'

function fmt(usd: number): string {
  if (usd >= 1_000_000_000) return `$${(usd / 1_000_000_000).toFixed(1)}B`
  if (usd >= 1_000_000) return `$${(usd / 1_000_000).toFixed(0)}M`
  if (usd >= 1_000) return `$${(usd / 1_000).toFixed(0)}K`
  return `$${usd.toFixed(2)}`
}

function fmtPrice(usd: number): string {
  if (usd >= 10000) return `$${usd.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
  if (usd >= 1) return `$${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
  return `$${usd.toPrecision(4)}`
}

function ChangeArrow({ positive }: { positive: boolean }) {
  return positive ? (
    <svg width="10" height="10" viewBox="0 0 8 8" fill="currentColor" className="inline -mt-px">
      <path d="M4 1L7 5H1L4 1Z" />
    </svg>
  ) : (
    <svg width="10" height="10" viewBox="0 0 8 8" fill="currentColor" className="inline -mt-px">
      <path d="M4 7L1 3H7L4 7Z" />
    </svg>
  )
}

// ─── Segmented profile bar ────────────────────────────────────────────────────

const SEGMENTS = 10

function SegmentedBar({ label, pct, display }: { label: string; pct: number; display: string }) {
  const filled = Math.round((Math.min(Math.max(pct, 0), 100) / 100) * SEGMENTS)
  const color = pct >= 75 ? '#22c55e' : pct >= 40 ? '#f59e0b' : '#ef4444'
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontFamily: sf, fontSize: 9, textTransform: 'uppercase' as const, letterSpacing: 1.5, color: MUTED }}>{label}</span>
        <span style={{ fontFamily: sf, fontSize: 11, fontWeight: 700, color }}>{display}</span>
      </div>
      <div style={{ display: 'flex', gap: 3 }}>
        {Array.from({ length: SEGMENTS }).map((_, i) => (
          <div key={i} style={{ flex: 1, height: 5, background: i < filled ? color : '#1c1c1e' }} />
        ))}
      </div>
    </div>
  )
}

// ─── Volume bar chart ─────────────────────────────────────────────────────────

const CHART_H = 80

function VolumeChart({ data }: { data: [number, number][] }) {
  if (!data.length) return null

  // CoinGecko exchange volume_chart?days=7 returns hourly points with Unix second timestamps.
  // Each point is a ROLLING 24h volume — must NOT be summed. Take the last value per day.
  const todayKey = new Date().toISOString().slice(0, 10)
  const buckets: Record<string, number> = {}
  for (const [ts, vol] of data) {
    const ms = ts < 1e12 ? ts * 1000 : ts
    const key = new Date(ms).toISOString().slice(0, 10)
    if (key <= todayKey) buckets[key] = vol
  }

  const days = Object.keys(buckets).sort().slice(-7)
  const points = days.map(d => ({ day: d, vol: buckets[d] }))

  const vols = points.map(p => p.vol)
  const max = Math.max(...vols)
  const min = Math.min(...vols)
  const avg = vols.reduce((s, v) => s + v, 0) / vols.length
  const peakIdx = vols.indexOf(max)

  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const labels = days.map(d => DAY_NAMES[new Date(d + 'T12:00:00Z').getUTCDay()])

  return (
    <div>
      <div className="flex items-end gap-1.5" style={{ height: CHART_H + 22, paddingTop: 22 }}>
        {vols.map((vol, i) => {
          const barH = max > 0 ? Math.max((vol / max) * CHART_H, 3) : 3
          const isToday = i === points.length - 1
          const isPeak = i === peakIdx
          return (
            <div key={i} className="flex-1 group/bar" style={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'flex-end' }}>
              <span style={{
                position: 'absolute',
                bottom: barH + 4,
                left: '50%',
                transform: 'translateX(-50%)',
                fontFamily: sf,
                fontSize: 9,
                fontWeight: isPeak || isToday ? 700 : 400,
                color: isToday ? '#22c55e' : isPeak ? '#fff' : '#71717a',

                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
              }}>
                {isPeak && !isToday && (
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="#22c55e" style={{ display: 'inline', marginBottom: 1 }}>
                    <path d="M4 1L7 5H1L4 1Z" />
                  </svg>
                )}
                {fmt(vol)}
              </span>
              <div
                className="w-full transition-colors group-hover/bar:bg-zinc-500"
                style={{
                  height: barH,
                  background: isToday ? '#71717a' : isPeak ? '#52525b' : '#27272a',
                }}
                title={fmt(vol)}
              />
            </div>
          )
        })}
      </div>

      <div className="flex gap-1.5 mt-1.5">
        {labels.map((label, i) => {
          const isToday = i === points.length - 1
          return (
            <div key={i} className="flex-1 flex items-center justify-center">
              <span style={{
                fontFamily: sf,
                fontSize: 9,
                fontWeight: isToday ? 700 : 400,
                color: isToday ? '#22c55e' : MUTED,
              }}>
                {isToday ? 'Today' : label}
              </span>
            </div>
          )
        })}
      </div>

      <div className="flex items-center justify-between mt-3" style={{ borderTop: '1px solid #18181b', paddingTop: 8 }}>
  <span style={{ fontFamily: sf, fontSize: 9, color: '#a1a1aa', textTransform: 'uppercase' as const, letterSpacing: 1.5 }}>Low: {fmt(min)}</span>
  <span style={{ fontFamily: sf, fontSize: 9, color: '#a1a1aa', textTransform: 'uppercase' as const, letterSpacing: 1.5 }}>Avg: {fmt(avg)}</span>
  <span style={{ fontFamily: sf, fontSize: 9, color: '#a1a1aa', textTransform: 'uppercase' as const, letterSpacing: 1.5 }}>Peak: {fmt(max)}</span>
</div>
    </div>
  )
}


function formatTicker(s: string): string {
  if (!s) return s
  if (s.startsWith('0x') || s.startsWith('0X')) return s.slice(0, 6) + '…'
  return s
}

// ─── Top pairs table (V2: card + compact list) ───────────────────────────────

function TopPairsTable({ topPairs }: { topPairs: ExchangePageData['topPairs'] }) {
  if (!topPairs.length) return null

  const totalVol = topPairs.reduce((s, p) => s + (p.converted_volume?.usd ?? 0), 0)
  const maxVol = topPairs[0]?.converted_volume?.usd ?? 1
  const top4 = topPairs.slice(0, 4)
  const rest = topPairs.slice(4)

  return (
    <div>
      <h2 className="text-zinc-400 text-xs uppercase tracking-widest mb-4" style={{ fontFamily: sf }}>
        Top trading pairs
      </h2>

      {/* ── Top 4 cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, background: '#27272a', border: '1px solid #27272a', marginBottom: 1 }}>
        {top4.map((p, i) => {
          const vol = p.converted_volume?.usd ?? 0
          const price = p.converted_last?.usd ?? p.last
          const sharePct = totalVol > 0 ? ((vol / totalVol) * 100).toFixed(1) : '0.0'
          const barW = maxVol > 0 ? (vol / maxVol) * 100 : 0
          return (
            <div key={i} style={{ background: '#000', padding: '16px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                <span style={{ fontFamily: sf, fontSize: 15, fontWeight: 800, color: '#fff' }}>{formatTicker(p.base)}</span>
                <span style={{ fontFamily: sf, fontSize: 13, color: MUTED }}>/{formatTicker(p.target)}</span>
                </div>
                <span style={{ fontFamily: sf, fontSize: 9, color: MUTED, textTransform: 'uppercase' as const, letterSpacing: 1.5, background: '#18181b', padding: '3px 7px' }}>
                  {sharePct}%
                </span>
              </div>
              <div style={{ marginBottom: 6 }}>
                <span style={{ fontFamily: sf, fontSize: 18, fontWeight: 700, color: '#fff' }}>{fmtPrice(price)}</span>
              </div>
              <div style={{ marginBottom: 6 }}>
                <span style={{ fontFamily: sf, fontSize: 11, color: '#22c55e', fontWeight: 700 }}>{fmt(vol)}</span>
                {p.bid_ask_spread_percentage > 0 && (
                  <span style={{ fontFamily: sf, fontSize: 10, color: MUTED, marginLeft: 8 }}>spread {p.bid_ask_spread_percentage.toFixed(3)}%</span>
                )}
              </div>
              <div style={{ height: 2, background: '#18181b' }}>
                <div style={{ height: '100%', width: `${barW}%`, background: '#22c55e' }} />
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Compact rest ── */}
      {rest.length > 0 && (
        <div style={{ border: '1px solid #27272a', borderTop: 'none' }}>
          {rest.map((p, i) => {
            const vol = p.converted_volume?.usd ?? 0
            const price = p.converted_last?.usd ?? p.last
            const sharePct = totalVol > 0 ? ((vol / totalVol) * 100).toFixed(1) : '0.0'
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '9px 18px', gap: 12, borderBottom: i < rest.length - 1 ? '1px solid #18181b' : 'none' }}
                className="hover:bg-zinc-900/40 transition-colors">
                <span style={{ fontFamily: sf, fontSize: 10, color: '#3f3f46', width: 16, flexShrink: 0 }}>{i + 5}</span>
                <span style={{ fontFamily: sf, fontSize: 12, fontWeight: 700, color: '#e4e4e7', flex: 1 }}>
                {formatTicker(p.base)}<span style={{ color: MUTED, fontWeight: 400 }}>/{formatTicker(p.target)}</span>
                </span>
                <span style={{ fontFamily: sf, fontSize: 11, color: MUTED }}>{fmtPrice(price)}</span>
                <span style={{ fontFamily: sf, fontSize: 11, fontWeight: 600, color: '#fff' }}>{fmt(vol)}</span>
                <span style={{ fontFamily: sf, fontSize: 10, color: MUTED, width: 36, textAlign: 'right' as const }}>{sharePct}%</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Fact row ─────────────────────────────────────────────────────────────────

function FactRow({ label, value, green }: { label: string; value: string; green?: boolean }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-zinc-800 last:border-0">
      <span style={{ color: MUTED, fontFamily: sf, fontSize: 12 }}>{label}</span>
      <span className="font-medium" style={{ fontFamily: sf, fontSize: 12, color: green ? '#22c55e' : '#e4e4e7' }}>
        {value}
      </span>
    </div>
  )
}

// ─── Social links ─────────────────────────────────────────────────────────────

export function SocialLinks({ detail }: { detail: ExchangePageData['detail'] }) {
  const links = [
    detail.twitter_handle && { label: 'X / Twitter', href: `https://twitter.com/${detail.twitter_handle}` },
    detail.reddit_url && { label: 'Reddit', href: detail.reddit_url },
    detail.telegram_url && { label: 'Telegram', href: detail.telegram_url },
    detail.facebook_url && { label: 'Facebook', href: detail.facebook_url },
    { label: 'Website', href: detail.url },
  ].filter(Boolean) as { label: string; href: string }[]

  if (!links.length) return null
  return (
    <div className="flex flex-wrap gap-2 mt-4">
      {links.map(({ label, href }) => (
        <a
          key={href}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="border border-zinc-800 hover:border-zinc-600 transition-colors px-3 py-1.5"
          style={{ color: MUTED, fontFamily: sf, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, textDecoration: 'none' }}
        >
          {label} ↗
        </a>
      ))}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ExchangePageClient({
  data,
  children,
  breadcrumbSuffix,
}: {
  data: ExchangePageData
  children?: React.ReactNode
  breadcrumbSuffix?: string
}) {
  const { detail, meta, volumeChartUsd, volume24hUsd, volumeChange7d, slug } = data

  const typeLabel = meta.type === 'dex' ? 'DEX' : 'CEX'

  const profileBars = [
    {
      label: 'Trust score',
      pct: detail.trust_score * 10,
      display: `${detail.trust_score}/10`,
    },
    {
      label: 'Liquidity',
      pct: Math.min((volume24hUsd / 200_000_000) * 100, 100),
      display: volume24hUsd > 5e9 ? 'Excellent' : volume24hUsd > 1e9 ? 'Good' : 'Average',
    },
    {
      label: 'Fees',
      pct: Math.max(0, 100 - meta.makerFee * 500),
      display: `${meta.makerFee}% maker`,
    },
    {
      label: 'US access',
      pct: meta.regionRestriction === 'global' ? 90 : meta.regionRestriction === 'us-restricted' ? 8 : 50,
      display: meta.regionRestriction === 'global' ? 'Available' : meta.regionRestriction === 'us-restricted' ? 'Blocked' : 'Partial',
    },
  ]

  const tabs = [
    { label: 'Overview',      href: `/exchanges/${slug}` },
   // { label: 'Review',        href: `/exchanges/${slug}/review` },
    { label: 'Fees',          href: `/exchanges/${slug}/fees` },
   // { label: 'Referral code', href: `/exchanges/${slug}/referral-code` },
   //  { label: 'Alternatives',  href: `/exchanges/${slug}/alternatives` },
   // { label: 'News',          href: `/exchanges/${slug}/news` },
  ]

  return (
    <main className="bg-black min-h-screen">
      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* Breadcrumb */}
<nav className="flex items-center gap-2 mb-6" style={{ fontFamily: sf, fontSize: 12, color: MUTED }}>
  <Link href="/" className="hover:text-white transition-colors" style={{ color: MUTED, textDecoration: 'none' }}>Crypto Street</Link>
  <span>/</span>
  <Link href="/exchanges" className="hover:text-white transition-colors" style={{ color: MUTED, textDecoration: 'none' }}>Exchanges</Link>
  <span>/</span>
  {breadcrumbSuffix ? (
    <Link href={`/exchanges/${detail.id}`} className="hover:text-white transition-colors" style={{ color: MUTED, textDecoration: 'none' }}>{detail.name}</Link>
  ) : (
    <span style={{ color: MUTED }}>{detail.name}</span>
  )}
  {breadcrumbSuffix && (
    <>
      <span>/</span>
      <span style={{ color: MUTED }}>{breadcrumbSuffix}</span>
    </>
  )}
</nav>

        {/* ═══ HERO ═══ */}
        <div className="mb-6">

          {/* ── Mobile ── */}
          <div className="lg:hidden flex flex-col gap-6">
          <div className="flex items-start gap-3">
  <div className="relative flex-shrink-0" style={{ width: 40, height: 40 }}>
    <Image src={detail.image} alt={detail.name} fill className="object-contain rounded-full" unoptimized />
  </div>
  <div className="flex-1 min-w-0">
    <div className="flex items-center gap-2 flex-wrap">
      <h1 className="text-white font-bold text-2xl" style={{ fontFamily: sf, margin: 0 }}>{detail.name}</h1>
      <span className="text-zinc-400 text-sm uppercase" style={{ fontFamily: sf }}>{typeLabel}</span>
    </div>
    <span className="text-zinc-400 text-xs" style={{ fontFamily: sf }}>Rank #{detail.trust_score_rank}</span>
  </div>
  {meta.referralUrl ? (
    <a href={meta.referralUrl} target="_blank" rel="noopener noreferrer"
      className="ml-auto inline-flex items-center gap-2 border border-green-500 text-green-500 px-4 py-1.5 text-xs uppercase tracking-widest hover:bg-green-500 hover:border-green-500 hover:text-black hover:font-bold transition-all duration-300 whitespace-nowrap flex-shrink-0"
      style={{ fontFamily: sf, textDecoration: 'none' }}>
      Trade now
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
    </a>
  ) : (
    <a href={detail.url} target="_blank" rel="noopener noreferrer"
    className="ml-auto inline-flex items-center gap-2 border border-green-500 text-green-500 px-4 py-1.5 text-xs uppercase tracking-widest hover:bg-green-500 hover:border-green-500 hover:text-black hover:font-bold transition-all duration-300 whitespace-nowrap flex-shrink-0"
    style={{ fontFamily: sf, textDecoration: 'none' }}>
    Trade now
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  </a>
  )}
</div>

            <div>
              <p className="text-zinc-400 uppercase tracking-widest" style={{ fontFamily: sf, fontSize: 10, marginBottom: 8 }}>24h trading volume</p>
              <div className="flex items-baseline gap-3">
                <p className="text-white font-bold tabular-nums" style={{ fontFamily: sf, fontSize: 32, lineHeight: 1 }}>{fmt(volume24hUsd)}</p>
                {volumeChange7d != null && (
                  <span className={`text-sm font-medium inline-flex items-center gap-1 ${volumeChange7d >= 0 ? 'text-green-500' : 'text-red-500'}`} style={{ fontFamily: sf }}>
                    <ChangeArrow positive={volumeChange7d >= 0} />
                    {volumeChange7d >= 0 ? '+' : '-'}{Math.abs(volumeChange7d).toFixed(1)}% 7d
                  </span>
                )}
              </div>
            </div>

            <div>
              <p className="text-zinc-400 uppercase tracking-widest" style={{ fontFamily: sf, fontSize: 10, marginBottom: 16 }}>Exchange profile</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {profileBars.map(bar => <SegmentedBar key={bar.label} {...bar} />)}
              </div>
            </div>

            {meta.referralBonus && meta.referralUrl && (
              <a href={meta.referralUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between transition-colors"
                style={{ padding: '10px 16px', background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.15)', textDecoration: 'none', gap: 12 }}>
                <div>
                  <div style={{ fontFamily: sf, fontSize: 13, fontWeight: 800, color: '#22c55e' }}>{meta.referralBonus}</div>
                  {meta.referralCode && (
                    <div style={{ fontFamily: sf, fontSize: 11, color: MUTED }}>Code: <span className="text-white font-bold">{meta.referralCode}</span></div>
                  )}
                </div>
                <span className="flex-shrink-0" style={{ fontFamily: sf, fontSize: 10, fontWeight: 700, color: '#22c55e', textTransform: 'uppercase', letterSpacing: 2, border: '1px solid rgba(34,197,94,0.4)', padding: '5px 12px' }}>Claim →</span>
              </a>
            )}
          </div>

          {/* ── Desktop ── */}
          <div className="hidden lg:grid" style={{
            gridTemplateColumns: '1fr 1px 1fr 1px 1fr',
            gridTemplateRows: 'auto auto',
          }}>

            {/* Col 1 — name + button */}
            <div style={{ paddingRight: 32, gridColumn: 1, gridRow: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 0 }}>
                <div className="relative flex-shrink-0" style={{ width: 48, height: 48 }}>
                  <Image src={detail.image} alt={detail.name} fill className="object-contain rounded-full" unoptimized />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <h1 className="text-white font-bold text-2xl" style={{ fontFamily: sf, margin: 0, whiteSpace: 'nowrap' }}>{detail.name}</h1>
                    <span className="text-zinc-400 text-sm uppercase" style={{ fontFamily: sf, whiteSpace: 'nowrap' }}>{typeLabel}</span>
                    <span style={{ flex: 1 }} />
                    {meta.referralUrl ? (
                      <a href={meta.referralUrl} target="_blank" rel="noopener noreferrer"
                        className="ml-auto inline-flex items-center gap-2 border border-green-500 text-green-500 px-4 py-1.5 text-xs uppercase tracking-widest hover:bg-green-500 hover:border-green-500 hover:text-black hover:font-bold transition-all duration-300 whitespace-nowrap"
                        style={{ fontFamily: sf, textDecoration: 'none' }}>
                        Trade now
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                      </a>
                    ) : (
                      <a href={detail.url} target="_blank" rel="noopener noreferrer"
                      className="ml-auto inline-flex items-center gap-2 border border-green-500 text-green-500 px-4 py-1.5 text-xs uppercase tracking-widest hover:bg-green-500 hover:border-green-500 hover:text-black hover:font-bold transition-all duration-300 whitespace-nowrap"
                      style={{ fontFamily: sf, textDecoration: 'none' }}>
                      Trade now
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </a>
                    )}
                  </div>
                  <span className="text-zinc-400 text-xs" style={{ fontFamily: sf }}>Rank #{detail.trust_score_rank}</span>
                </div>
              </div>
            </div>

            {/* Divider 1 — fixed height matching col 1/2 content */}
            <div style={{ gridColumn: 2, gridRow: 1, alignSelf: 'start', width: 1, height: 90, background: '#27272a' }} />

            {/* Col 2 — volume */}
            <div style={{ padding: '0 32px', gridColumn: 3, gridRow: 1 }}>
              <p className="text-zinc-400 uppercase tracking-widest" style={{ fontFamily: sf, fontSize: 10, marginBottom: 8 }}>24h trading volume</p>
              <p className="text-white font-bold tabular-nums" style={{ fontFamily: sf, fontSize: 36, lineHeight: 1, marginBottom: 6 }}>{fmt(volume24hUsd)}</p>
              {volumeChange7d != null && (
                <span className={`text-sm font-medium inline-flex items-center gap-1 ${volumeChange7d >= 0 ? 'text-green-500' : 'text-red-500'}`} style={{ fontFamily: sf }}>
                  <ChangeArrow positive={volumeChange7d >= 0} />
                  {volumeChange7d >= 0 ? '+' : '-'}{Math.abs(volumeChange7d).toFixed(1)}% 7d
                </span>
              )}
            </div>

            {/* Divider 2 — fixed height matching col 1/2 content */}
            <div style={{ gridColumn: 4, gridRow: 1, alignSelf: 'start', width: 1, height: 90, background: '#27272a' }} />

            {/* Col 3 — profile bars — spans both rows */}
            <div style={{ paddingLeft: 32, gridColumn: 5, gridRow: '1 / 3' }}>
              <p className="text-zinc-400 uppercase tracking-widest mb-4" style={{ fontFamily: sf, fontSize: 10 }}>Exchange profile</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {profileBars.map(bar => <SegmentedBar key={bar.label} {...bar} />)}
              </div>
            </div>

            {/* Referral banner — cols 1+2 only (grid cols 1–3), row 2 */}
            <div style={{ gridColumn: '1 / 4', gridRow: 2, paddingTop: 0 }}>
              {meta.referralBonus && meta.referralUrl ? (
                <a href={meta.referralUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-between transition-colors"
                  style={{ padding: '10px 20px', background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.15)', textDecoration: 'none', gap: 16 }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(34,197,94,0.35)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(34,197,94,0.15)')}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <span style={{ fontFamily: sf, fontSize: 9, color: MUTED, textTransform: 'uppercase', letterSpacing: 2, whiteSpace: 'nowrap' }}>Referral offer</span>
                    <span style={{ width: 1, height: 12, background: '#3f3f46', display: 'inline-block', flexShrink: 0, margin: '0 4px' }} />
                    <span style={{ fontFamily: sf, fontSize: 13, fontWeight: 800, color: '#22c55e', whiteSpace: 'nowrap' }}>{meta.referralBonus}</span>
                    {meta.referralCode && (
                      <span style={{ fontFamily: sf, fontSize: 11, color: MUTED, whiteSpace: 'nowrap' }}>
                        Code: <span className="text-white font-bold">{meta.referralCode}</span>
                      </span>
                    )}
                  </div>
                  <span className="flex-shrink-0" style={{ fontFamily: sf, fontSize: 10, fontWeight: 700, color: '#22c55e', textTransform: 'uppercase', letterSpacing: 2, border: '1px solid rgba(34,197,94,0.4)', padding: '5px 14px', whiteSpace: 'nowrap' }}>Claim →</span>
                </a>
              ) : null}
            </div>

          </div>
        </div>

        {/* ═══ TABS ═══ */}
        <ExchangeTabs tabs={tabs} />

        {/* ═══ TAB CONTENT ═══ */}
        {children ?? <OverviewContent data={data} />}

        {/* Back to exchanges */}
        <div className="border-t border-zinc-800 pt-6 pb-8 mt-8">
          <Link href="/exchanges" className="text-zinc-400 text-sm hover:text-white transition-colors inline-flex items-center gap-2" style={{ fontFamily: sf }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
            Back to exchanges
          </Link>
        </div>

      </div>
    </main>
  )
}

// ─── Overview tab content ─────────────────────────────────────────────────────

function OverviewContent({ data }: { data: ExchangePageData }) {
  const { detail, meta, about, volumeChartUsd, topPairs, slug } = data

  const regionLabel =
    meta.regionRestriction === 'us-restricted' ? 'No US'
    : meta.regionRestriction === 'global' ? 'Global'
    : 'Varies'

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

      {/* ── Left — 2/3 ── */}
      <div className="lg:col-span-2 space-y-10">

        {/* Volume chart */}
        {volumeChartUsd.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-zinc-400 text-xs uppercase tracking-widest" style={{ fontFamily: sf }}>7-day trading volume</h2>
              <span style={{ color: MUTED, fontFamily: sf, fontSize: 11 }}>USD</span>
            </div>
            <VolumeChart data={volumeChartUsd} />
          </div>
        )}

        {/* Top pairs */}
        <TopPairsTable topPairs={topPairs} />

        {/* About — plain, no box */}
        {about && (
          <div>
            <h2 className="text-zinc-300 text-xl lg:text-3xl font-semibold mb-4 font-[family-name:var(--font-saira)]">
              About {detail.name}
            </h2>
            <p className="text-zinc-400 text-sm leading-relaxed max-w-3xl">{about}</p>
            <SocialLinks detail={detail} />
          </div>
        )}

        {/* FAQ */}
        <div>
        <h2 className="text-zinc-300 text-xl lg:text-3xl font-semibold mb-5 font-[family-name:var(--font-saira)]">
            {detail.name} FAQ
          </h2>
          <div className="border border-zinc-800 divide-y divide-zinc-800">
            {[
              {
                q: `Is ${detail.name} a safe exchange?`,
                a: `${detail.name} has a trust score of ${detail.trust_score}/10 on CoinGecko, ranking #${detail.trust_score_rank} globally. Trust score is based on web traffic, volume legitimacy, cybersecurity practices, and API data quality.`,
              },
              {
                q: `What are ${detail.name} trading fees?`,
                a: `${detail.name} charges a maker fee of ${meta.makerFee}% and a taker fee of ${meta.takerFee}%. Fees may be reduced by holding the exchange's native token or using a referral code.`,
              },
              {
                q: `Does ${detail.name} require KYC?`,
                a: meta.kycRequired
                  ? `Yes, ${detail.name} requires identity verification (KYC) to access full trading and withdrawal features.`
                  : `${detail.name} does not require full KYC verification for basic trading, though some features may require identity checks.`,
              },
              {
                q: `Does ${detail.name} offer derivatives trading?`,
                a: meta.derivatives
                  ? `Yes, ${detail.name} offers derivatives trading including futures and perpetual contracts in addition to spot markets.`
                  : `${detail.name} currently focuses on spot trading and does not offer derivatives products.`,
              },
            ].map(({ q, a }) => (
              <div key={q} className="px-5 py-4">
                <h3 className="text-white text-sm font-bold mb-1.5" style={{ fontFamily: sf }}>{q}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* ── Right sidebar — 1/3 ── */}
      <div className="space-y-4">

        {/* Quick facts */}
        <div className="border border-zinc-800 px-5 py-4">
          <h2 className="text-white font-semibold mb-3" style={{ fontFamily: sf, fontSize: 11, textTransform: 'uppercase', letterSpacing: 2 }}>
            Quick facts
          </h2>
          <FactRow label="Type" value={meta.type === 'dex' ? 'DEX' : 'CEX'} />
          {detail.year_established && <FactRow label="Founded" value={String(detail.year_established)} />}
          {detail.country && <FactRow label="Country" value={detail.country} />}
          <FactRow label="Maker fee" value={`${meta.makerFee}%`} />
          <FactRow label="Taker fee" value={`${meta.takerFee}%`} />
          <FactRow label="KYC" value={meta.kycRequired ? 'Required' : 'Optional'} green={!meta.kycRequired} />
          <FactRow label="Derivatives" value={meta.derivatives ? 'Yes' : 'No'} green={meta.derivatives} />
          <FactRow label="Region" value={regionLabel} />
          <div className="mt-4">
            <Link href={`/exchanges/${slug}/fees`}
              className="block text-center border border-zinc-800 hover:border-zinc-600 transition-colors py-2.5"
              style={{ color: MUTED, fontFamily: sf, fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, textDecoration: 'none' }}>
              Full fee breakdown →
            </Link>
          </div>
        </div>

        {/* Referral bonus card */}
       {/* {meta.referralBonus && meta.referralUrl && (
          <div style={{ border: '1px solid rgba(34,197,94,0.2)', background: 'rgba(34,197,94,0.03)', padding: '18px 20px' }}>
            <div style={{ color: MUTED, fontFamily: sf, fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>Referral bonus</div>
            <div style={{ color: '#22c55e', fontFamily: sf, fontSize: 20, fontWeight: 800, marginBottom: 6, lineHeight: 1.2 }}>{meta.referralBonus}</div>
            {meta.referralCode && (
              <div style={{ color: MUTED, fontFamily: sf, fontSize: 11, marginBottom: 14 }}>
                Code: <span className="text-white font-bold">{meta.referralCode}</span>
              </div>
            )}
            <a href={meta.referralUrl} target="_blank" rel="noopener noreferrer"
              className="block text-center transition-colors"
              style={{ border: '1px solid rgba(34,197,94,0.4)', color: '#22c55e', fontFamily: sf, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, padding: '9px 0', textDecoration: 'none', marginBottom: 8 }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(34,197,94,0.08)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              Claim bonus →
            </a>
            <Link href={`/exchanges/${slug}/referral-code`}
              className="block text-center transition-colors"
              style={{ color: MUTED, fontFamily: sf, fontSize: 10, textDecoration: 'none' }}>
              How to use the code →
            </Link>
          </div>
        )}
          */}

        {/* Alternatives teaser */}
       {/* <Link href={`/exchanges/${slug}/alternatives`}
          className="block border border-zinc-800 hover:border-zinc-600 transition-colors px-5 py-4"
          style={{ textDecoration: 'none' }}>
          <div style={{ color: MUTED, fontFamily: sf, fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>Looking for alternatives?</div>
          <div className="text-white text-sm font-medium" style={{ fontFamily: sf }}>Compare exchanges similar to {detail.name} →</div>
        </Link> 
        */}

      </div>
    </div>
  )
}